import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import Header from "@/components/Header";
import StatCard from "@/components/StatCard";
import TradesTable from "@/components/TradesTable";
import CreateClientDialog from "@/components/CreateClientDialog";
import FundsDialog from "@/components/FundsDialog";
import ClientDetailDialog from "@/components/ClientDetailDialog";
import EditClientDialog from "@/components/EditClientDialog";
import EditTradeDialog from "@/components/EditTradeDialog";
import TradeEntryForm from "@/components/TradeEntryForm";
import { money, pnlFmt, pnlColor } from "@/lib/format";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wallet, Users, TrendingUp, Activity, ArrowDownToLine, Pencil, Trash2 } from "lucide-react";

export default function AdminDashboard() {
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [trades, setTrades] = useState([]);
  const [tab, setTab] = useState("open");
  const [fundsTarget, setFundsTarget] = useState(null);
  const [closing, setClosing] = useState(null);
  const [closePrice, setClosePrice] = useState("");
  const [closeProfit, setCloseProfit] = useState("");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [editTrade, setEditTrade] = useState(null);
  const [tradeDeleting, setTradeDeleting] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, c, t] = await Promise.all([
        api.get("/admin/summary"),
        api.get("/admin/clients"),
        api.get("/admin/trades"),
      ]);
      setSummary(s.data);
      setClients(c.data);
      setTrades(t.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openTrades = trades.filter((t) => t.status === "open");
  const closedTrades = trades.filter((t) => t.status === "closed");
  const filteredClients = clients.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase()) ||
      String(c.account_no).includes(search)
  );

  const submitClose = async () => {
    if (!closePrice) {
      toast.error("Close price is required");
      return;
    }
    try {
      await api.post(`/admin/trades/${closing.id}/close`, {
        close_price: parseFloat(closePrice),
        profit: closeProfit ? parseFloat(closeProfit) : null,
      });
      toast.success(`Trade #${closing.ticket} closed — P&L allocated to clients`);
      setClosing(null);
      setClosePrice("");
      setCloseProfit("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const confirmRemoveTrade = async () => {
    const t = tradeDeleting;
    try {
      await api.delete(`/admin/trades/${t.id}`);
      toast.success(`Trade #${t.ticket} deleted${t.status === "closed" ? " — client balances reversed" : ""}`);
      setTradeDeleting(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteClient = async () => {
    try {
      await api.delete(`/admin/clients/${deleting.id}`);
      toast.success(`Client ${deleting.name} deleted`);
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0E14]" data-testid="admin-dashboard">
      <Header />
      <main className="mx-auto max-w-[1500px] space-y-6 px-4 py-6 sm:px-6">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5 fade-up">
          <StatCard testid="stat-aum" label="Fund Balance (AUM)" value={money(summary?.total_balance ?? 0)} icon={Wallet} sub={`${summary?.closed_trades ?? 0} closed trades`} />
          <StatCard testid="stat-clients" label="Active Clients" value={summary?.client_count ?? 0} icon={Users} accent="#EAECEF" />
          <StatCard
            testid="stat-realized-pnl"
            label="Realized P&L"
            value={pnlFmt(summary?.realized_pnl ?? 0)}
            icon={TrendingUp}
            accent={(summary?.realized_pnl ?? 0) >= 0 ? "#0ECB81" : "#F6465D"}
          />
          <StatCard testid="stat-open-positions" label="Open Positions" value={summary?.open_positions ?? 0} icon={Activity} accent="#EAECEF" />
          <StatCard testid="stat-deposits" label="Total Deposits" value={money(summary?.total_deposits ?? 0)} icon={ArrowDownToLine} sub={`Withdrawn: ${money(summary?.total_withdrawals ?? 0)}`} />
        </div>

        <div className="grid gap-6 xl:grid-cols-3">
          <div className="min-w-0 space-y-3 xl:col-span-2 fade-up">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-heading text-lg font-semibold text-[#EAECEF]">Client Accounts</h2>
              <div className="flex items-center gap-2">
                <input
                  data-testid="client-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search clients..."
                  className="rounded-md border border-[#2A2E39] bg-[#1E222D] px-3 py-2 text-sm text-[#EAECEF] placeholder:text-[#5E6673] focus:border-[#F0B90B]/50 focus:outline-none"
                />
                <CreateClientDialog onCreated={load} />
              </div>
            </div>
            <div className="overflow-x-auto rounded-lg border border-[#2A2E39] bg-[#131722]">
              <table data-testid="client-management-table" className="w-full min-w-[820px] text-sm">
                <thead>
                  <tr className="border-b border-[#2A2E39]">
                    {["Account", "Client", "Balance", "Net Deposits", "Total P&L", "Open", "Actions"].map((h) => (
                      <th key={h} className="whitespace-nowrap px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-[#848E9C]">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-3 py-10 text-center text-sm text-[#5E6673]">
                        No clients found
                      </td>
                    </tr>
                  )}
                  {filteredClients.map((c) => (
                    <tr key={c.id} data-testid={`client-row-${c.account_no}`} className="border-b border-[#1E222D] transition-colors hover:bg-[#1E222D]/60">
                      <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-[#F0B90B]">#{c.account_no}</td>
                      <td className="whitespace-nowrap px-3 py-2">
                        <button
                          data-testid={`client-view-btn-${c.account_no}`}
                          onClick={() => setDetail(c)}
                          className="text-left font-medium text-[#EAECEF] underline-offset-4 transition-colors hover:text-[#F0B90B] hover:underline"
                        >
                          {c.name}
                        </button>
                        <div className="text-xs text-[#5E6673]">{c.email}</div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right font-mono font-semibold text-[#EAECEF]">{money(c.balance)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-[#848E9C]">{money(c.total_deposits - c.total_withdrawals)}</td>
                      <td className={`whitespace-nowrap px-3 py-2 text-right font-mono font-semibold ${pnlColor(c.total_pnl)}`}>{pnlFmt(c.total_pnl)}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-center font-mono text-[#848E9C]">{c.open_positions}</td>
                      <td className="whitespace-nowrap px-3 py-2">
                        <div className="flex gap-1">
                          <button
                            data-testid={`client-edit-btn-${c.account_no}`}
                            onClick={() => setEditing(c)}
                            title="Edit client details"
                            className="rounded border border-[#2A2E39] bg-[#1E222D] px-2 py-1 text-[#848E9C] transition-colors hover:bg-[#2A2E39] hover:text-[#F0B90B]"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                          <button
                            data-testid={`client-deposit-btn-${c.account_no}`}
                            onClick={() => setFundsTarget({ client: c, mode: "deposit" })}
                            className="rounded border border-[#0ECB81]/40 bg-[#0ECB81]/10 px-2 py-1 text-[11px] font-semibold text-[#0ECB81] transition-colors hover:bg-[#0ECB81]/20"
                          >
                            Deposit
                          </button>
                          <button
                            data-testid={`client-withdraw-btn-${c.account_no}`}
                            onClick={() => setFundsTarget({ client: c, mode: "withdrawal" })}
                            className="rounded border border-[#F6465D]/40 bg-[#F6465D]/10 px-2 py-1 text-[11px] font-semibold text-[#F6465D] transition-colors hover:bg-[#F6465D]/20"
                          >
                            Withdraw
                          </button>
                          <button
                            data-testid={`client-delete-btn-${c.account_no}`}
                            onClick={() => setDeleting(c)}
                            title="Delete client account"
                            className="rounded border border-[#2A2E39] bg-[#1E222D] px-2 py-1 text-[#848E9C] transition-colors hover:bg-[#F6465D]/20 hover:text-[#F6465D]"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="min-w-0 fade-up">
            <TradeEntryForm clients={clients} onDone={load} />
          </div>
        </div>

        <div className="space-y-3 fade-up">
          <div className="flex items-center gap-2">
            <button
              data-testid="tab-open-positions"
              onClick={() => setTab("open")}
              className={`rounded-md px-4 py-2 text-xs font-bold tracking-wide transition-colors ${
                tab === "open" ? "bg-[#F0B90B] text-black" : "border border-[#2A2E39] bg-[#1E222D] text-[#848E9C] hover:bg-[#2A2E39]"
              }`}
            >
              OPEN POSITIONS ({openTrades.length})
            </button>
            <button
              data-testid="tab-closed-trades"
              onClick={() => setTab("closed")}
              className={`rounded-md px-4 py-2 text-xs font-bold tracking-wide transition-colors ${
                tab === "closed" ? "bg-[#F0B90B] text-black" : "border border-[#2A2E39] bg-[#1E222D] text-[#848E9C] hover:bg-[#2A2E39]"
              }`}
            >
              CLOSED TRADES ({closedTrades.length})
            </button>
          </div>
          <TradesTable
            trades={tab === "open" ? openTrades : closedTrades}
            variant="admin"
            testid="mt5-admin-trade-history-table"
            onClose={(t) => {
              setClosing(t);
              setClosePrice("");
              setCloseProfit("");
            }}
            onEdit={(t) => setEditTrade(t)}
            onDelete={(t) => setTradeDeleting(t)}
          />
        </div>
      </main>

      <FundsDialog target={fundsTarget} onClose={() => setFundsTarget(null)} onDone={load} />
      <ClientDetailDialog client={detail} onClose={() => setDetail(null)} />
      <EditClientDialog client={editing} onClose={() => setEditing(null)} onDone={load} />
      <EditTradeDialog trade={editTrade} onClose={() => setEditTrade(null)} onDone={load} />

      <Dialog open={!!tradeDeleting} onOpenChange={(o) => !o && setTradeDeleting(null)}>
        <DialogContent data-testid="delete-trade-modal" className="border-[#2A2E39] bg-[#131722] text-[#EAECEF] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-[#F6465D]">
              Delete Trade <span className="font-mono">#{tradeDeleting?.ticket}</span>
            </DialogTitle>
            <DialogDescription className="text-sm text-[#848E9C]">
              {tradeDeleting?.status === "closed"
                ? "This closed trade will be deleted and its P&L will be REVERSED from all allocated client balances. This action cannot be undone."
                : "This open position will be deleted from all allocated client accounts. This action cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <button
              data-testid="cancel-delete-trade-btn"
              onClick={() => setTradeDeleting(null)}
              className="flex-1 rounded-md border border-[#2A2E39] bg-[#1E222D] py-2.5 text-sm font-semibold text-[#EAECEF] transition-colors hover:bg-[#2A2E39]"
            >
              Cancel
            </button>
            <button
              data-testid="confirm-delete-trade-btn"
              onClick={confirmRemoveTrade}
              className="flex-1 rounded-md bg-[#F6465D] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#FF536A]"
            >
              Delete Trade
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent data-testid="delete-client-modal" className="border-[#2A2E39] bg-[#131722] text-[#EAECEF] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-[#F6465D]">Delete Client Account</DialogTitle>
            <DialogDescription className="text-sm text-[#848E9C]">
              This will permanently delete <span className="font-semibold text-[#EAECEF]">{deleting?.name}</span> (#{deleting?.account_no}), including all trade history and deposit/withdrawal records. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <button
              data-testid="cancel-delete-client-btn"
              onClick={() => setDeleting(null)}
              className="flex-1 rounded-md border border-[#2A2E39] bg-[#1E222D] py-2.5 text-sm font-semibold text-[#EAECEF] transition-colors hover:bg-[#2A2E39]"
            >
              Cancel
            </button>
            <button
              data-testid="confirm-delete-client-btn"
              onClick={deleteClient}
              className="flex-1 rounded-md bg-[#F6465D] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#FF536A]"
            >
              Delete Account
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!closing} onOpenChange={(o) => !o && setClosing(null)}>
        <DialogContent data-testid="close-trade-modal" className="border-[#2A2E39] bg-[#131722] text-[#EAECEF] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">
              Close Trade <span className="font-mono text-[#F0B90B]">#{closing?.ticket}</span>
            </DialogTitle>
            <DialogDescription className="text-sm text-[#848E9C]">
              Enter the close price. P&L is auto-computed and allocated pro-rata to the trade's clients.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-md border border-[#2A2E39] bg-[#1E222D] px-3 py-2 text-sm">
              <span className={closing?.type === "buy" ? "font-mono font-bold text-[#0ECB81]" : "font-mono font-bold text-[#F6465D]"}>
                {closing?.type?.toUpperCase()} {closing?.symbol}
              </span>
              <span className="font-mono text-[#848E9C]">{Number(closing?.volume ?? 0).toFixed(4)} lots @ {closing?.open_price}</span>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-medium uppercase tracking-wider text-[#848E9C]">Close Price</Label>
              <Input
                data-testid="close-trade-price-input"
                type="number"
                step="0.01"
                value={closePrice}
                onChange={(e) => setClosePrice(e.target.value)}
                placeholder="e.g. 2375.40"
                className="border-[#2A2E39] bg-[#1E222D] font-mono text-[#EAECEF] focus-visible:ring-[#F0B90B]/50"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-medium uppercase tracking-wider text-[#848E9C]">P&L Override (optional)</Label>
              <Input
                data-testid="close-trade-pnl-input"
                type="number"
                step="0.01"
                value={closeProfit}
                onChange={(e) => setCloseProfit(e.target.value)}
                placeholder="Auto-computed from prices"
                className="border-[#2A2E39] bg-[#1E222D] font-mono text-[#EAECEF] focus-visible:ring-[#F0B90B]/50"
              />
            </div>
            <button
              data-testid="confirm-close-trade-btn"
              onClick={submitClose}
              className="w-full rounded-md bg-[#F0B90B] py-2.5 text-sm font-semibold text-black shadow-[0_0_12px_rgba(240,185,11,0.25)] transition-colors hover:bg-[#F8D12F]"
            >
              Close Position & Allocate P&L
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
